import React, { useEffect, useMemo, useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Spinner, Input, chakra } from '@chakra-ui/react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  getSortedRowModel
} from '@tanstack/react-table';
import { ResponsiveFiltersHeader } from './ResponsiveFiltersHeader';
import IconComponent from '../IconComponent/IconComponent';

interface StickyTableProps<Data> {
  data: Data[];
  columns: any;
  isLoading?: boolean;
  striped?: boolean;
  noVerticalLineIndices?: number[];
  filtersLeftContent?: React.ReactNode;
  filtersRightContent?: React.ReactNode;
  enableSearch?: boolean;
  showClearFilters?: boolean;
  searchPlaceholder?: string;
  onVisibleRowsChange?: (visibleRows: Data[]) => void;
  searchStorageKey?: string;
  sortingStorageKey?: string;
}

export function StickyScrollableTableContainer<Data extends object>({
  data,
  columns,
  isLoading = false,
  striped = true,
  noVerticalLineIndices = [],
  filtersLeftContent,
  filtersRightContent,
  enableSearch = true,
  showClearFilters = true,
  searchPlaceholder = 'Szukaj...',
  onVisibleRowsChange,
  searchStorageKey,
  sortingStorageKey
}: StickyTableProps<Data>) {
  const [searchPhrase, setSearchPhrase] = useState(() => {
    if (typeof window === 'undefined') return '';
    return searchStorageKey ? sessionStorage.getItem(searchStorageKey) || '' : '';
  });

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (typeof window === 'undefined') return [];
    if (sortingStorageKey) {
      try {
        return JSON.parse(sessionStorage.getItem(sortingStorageKey) || '[]');
      } catch {
        return [];
      }
    }
    return [];
  });

  const filteredData = useMemo(() => {
    if (!searchPhrase) return data;

    const searchWords = searchPhrase.toLowerCase().trim().split(/\s+/);

    return data.filter((row) => {
      const valuesToSearch = Object.keys(row).flatMap((key) => {
        const value = row[key as keyof Data];

        if (Array.isArray(value)) {
          return value.flatMap((item) => {
            if (typeof item === 'object' && item !== null) {
              // tablica obiektów → np. [{firstName, lastName}]
              return Object.values(item);
            }
            return item; // tablica stringów/liczb
          });
        }

        if (typeof value === 'object' && value !== null) {
          return Object.values(value);
        }

        return [value];
      });

      const combined = valuesToSearch.map((v) => String(v ?? '').toLowerCase()).join(' ');

      return searchWords.every((word) => combined.includes(word));
    });
  }, [data, searchPhrase]);

  const clearSessionStorage = () => {
    if (searchStorageKey) {
      sessionStorage.removeItem(searchStorageKey);
    }
    if (sortingStorageKey) {
      sessionStorage.removeItem(sortingStorageKey);
    }

    // opcjonalnie resetuj lokalny state
    setSearchPhrase('');
    setSorting([]);
  };

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
    onSortingChange: (updater) => {
      const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(nextSorting);

      if (sortingStorageKey) {
        sessionStorage.setItem(sortingStorageKey, JSON.stringify(nextSorting));
      }
    }
  });

  useEffect(() => {
    if (onVisibleRowsChange) {
      const rows = table.getRowModel().rows.map((row) => row.original);
      onVisibleRowsChange(rows);
    }
  }, [table.getRowModel().rows]);

  return (
    <Box width="100%">
      <ResponsiveFiltersHeader
        leftContent={
          <>
            {enableSearch && (
              <Input
                value={searchPhrase}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchPhrase(value);
                  if (searchStorageKey) {
                    sessionStorage.setItem(searchStorageKey, value);
                  }
                }}
                placeholder={searchPlaceholder}
                width={{ base: '100%', md: '300px' }}
              />
            )}
            {filtersLeftContent}
          </>
        }
        rightContent={filtersRightContent}
        onClearFilters={clearSessionStorage}
        showClearFilters={showClearFilters}
      />

      <Box
        overflowX="auto"
        overflowY="auto"
        maxH="calc(100vh - 320px)"
        borderRadius="md"
        minW="100%">
        <Table
          variant="simple"
          size="sm"
          className="table-auto"
          style={{ tableLayout: 'fixed', minWidth: '950px' }}>
          <Thead className="sticky top-0 bg-grayLight-25 border-gray-300 z-10 shadow-sm cursor-pointer">
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <Th
                    textTransform="unset"
                    onClick={header.column.getToggleSortingHandler()}
                    key={header.id}
                    style={{
                      width: header.column.getSize() ? `${header.column.getSize()}px` : 'auto',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    borderRight={
                      noVerticalLineIndices.includes(index)
                        ? 'none'
                        : '1px solid var(--chakra-colors-gray-200)'
                    }>
                    <Box className="py-3" display="flex" alignItems="center">
                      {flexRender(header.column.columnDef.header, header.getContext())}

                      <span className="pl-1">
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === 'desc' ? (
                            <IconComponent iconName="arrow" />
                          ) : (
                            <IconComponent iconName="arrow" className="rotate-180" />
                          )
                        ) : null}
                      </span>
                    </Box>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center">
                  Brak danych do wyświetlenia
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row, rowIndex) => (
                <Tr
                  key={row.id}
                  className={striped && rowIndex % 2 === 1 ? 'bg-brand-50' : undefined}>
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <Td
                      key={cell.id}
                      borderRight={
                        noVerticalLineIndices.includes(cellIndex)
                          ? 'none'
                          : '1px solid var(--chakra-colors-gray-100)'
                      }
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
